import { focusManager, QueryClient } from '@tanstack/react-query'
import { miniApp } from '@tma.js/sdk-react'
import { useEffect } from 'react'
import { createBrowserRouter, useRouteError } from 'react-router'
import { ApiError, errorMessage } from './api/client.ts'
import { useMe } from './api/hooks.ts'
import { readSession, writeSession } from './lib/storage.ts'
import { HomeScreen } from './screens/Home.tsx'
import { AuthProblem, NotFoundScreen, WatchingScreen } from './screens/Misc.tsx'
import { AboutScreen, MoreScreen } from './screens/More.tsx'
import { MyTasksScreen } from './screens/MyTasks.tsx'
import { OutboxScreen } from './screens/Outbox.tsx'
import { TaskDetailScreen } from './screens/TaskDetail.tsx'
import { TaskFormScreen } from './screens/TaskForm.tsx'
import { TeamMemberScreen, TeamScreen } from './screens/Team.tsx'
import { getTelegramEnv } from './telegram/sdk.ts'
import { AppShell } from './ui/AppShell.tsx'
import { ErrorState } from './ui/EmptyState.tsx'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: true,
      // 4xx не повторяем — это не временная ошибка
      retry: (count, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && count < 2,
    },
    mutations: { retry: false },
  },
})

/** Свернули/развернули Mini App — для React Query это потеря/возврат фокуса (обновим списки). */
function useTelegramFocus(): void {
  useEffect(() => {
    if (!getTelegramEnv().native) return
    try {
      return miniApp.isActive.sub(() => focusManager.setFocused(miniApp.isActive()))
    } catch {
      return
    }
  }, [])
}

function Root() {
  const me = useMe()
  useTelegramFocus()
  if (me.error instanceof ApiError && (me.error.code === 'UNAUTHORIZED' || me.error.code === 'FORBIDDEN')) {
    return <AuthProblem text={errorMessage(me.error)} onRetry={() => void me.refetch()} />
  }
  return <AppShell />
}

function RouteError() {
  const error = useRouteError()
  if (import.meta.env.DEV) console.error(error)
  return (
    <div className="fatal">
      <ErrorState
        text="Что-то сломалось на этом экране. Обновите приложение — данные не потеряются."
        onRetry={() => window.location.replace('/')}
      />
    </div>
  )
}

export function createAppRouter() {
  return createBrowserRouter([
    {
      path: '/',
      element: <Root />,
      errorElement: <RouteError />,
      // пока подгружается ленивый экран при первом открытии (deep link на редкий экран)
      hydrateFallbackElement: (
        <div className="fatal" role="status" aria-label="Загрузка">
          <span className="spinner" aria-hidden="true" />
        </div>
      ),
      // редкие экраны грузим по требованию — меньше первый бандл
      children: [
        { index: true, element: <MyTasksScreen /> },
        { path: 'home', element: <HomeScreen /> },
        { path: 'outbox', element: <OutboxScreen /> },
        { path: 'team', element: <TeamScreen /> },
        { path: 'team/:userId', element: <TeamMemberScreen /> },
        { path: 'more', element: <MoreScreen /> },
        { path: 'tasks/new', element: <TaskFormScreen /> },
        { path: 'tasks/:id', element: <TaskDetailScreen /> },
        { path: 'tasks/:id/edit', element: <TaskFormScreen /> },
        {
          path: 'drafts/:batchId',
          lazy: () => import('./screens/DraftReview.tsx').then((m) => ({ Component: m.DraftReviewScreen })),
        },
        {
          path: 'reports',
          lazy: () => import('./screens/Reports.tsx').then((m) => ({ Component: m.ReportsScreen })),
        },
        {
          path: 'recurring',
          lazy: () => import('./screens/Recurring.tsx').then((m) => ({ Component: m.RecurringListScreen })),
        },
        {
          path: 'recurring/:id',
          lazy: () => import('./screens/Recurring.tsx').then((m) => ({ Component: m.RecurringFormScreen })),
        },
        { path: 'watching', element: <WatchingScreen /> },
        {
          path: 'ideas',
          lazy: () => import('./screens/Processes.tsx').then((m) => ({ Component: m.IdeasScreen })),
        },
        {
          path: 'approvals',
          lazy: () => import('./screens/Processes.tsx').then((m) => ({ Component: m.ApprovalsScreen })),
        },
        {
          path: 'approvals/new',
          lazy: () => import('./screens/Processes.tsx').then((m) => ({ Component: m.ApprovalFormScreen })),
        },
        {
          path: 'shifts',
          lazy: () => import('./screens/Processes.tsx').then((m) => ({ Component: m.ShiftsScreen })),
        },
        {
          path: 'projects',
          lazy: () => import('./screens/Projects.tsx').then((m) => ({ Component: m.ProjectsScreen })),
        },
        {
          path: 'projects/:id',
          lazy: () => import('./screens/Projects.tsx').then((m) => ({ Component: m.ProjectTasksScreen })),
        },
        {
          path: 'settings',
          lazy: () => import('./screens/Settings.tsx').then((m) => ({ Component: m.SettingsScreen })),
        },
        {
          path: 'admin',
          lazy: () => import('./screens/Admin.tsx').then((m) => ({ Component: m.AdminScreen })),
        },
        {
          path: 'admin/users/:id',
          lazy: () => import('./screens/Admin.tsx').then((m) => ({ Component: m.AdminUserScreen })),
        },
        { path: 'about', element: <AboutScreen /> },
        { path: '*', element: <NotFoundScreen /> },
      ],
    },
  ])
}

type AppRouter = ReturnType<typeof createAppRouter>

/**
 * Deep link (startapp=task_123 | draft_<id> | new | review): кладём экран поверх «Моих задач»,
 * чтобы «Назад» вёл на главную. Один раз за сессию — перезагрузка не открывает экран повторно.
 */
export function applyStartParam(router: AppRouter, startParam: string | null): void {
  if (!startParam || readSession('startHandled') === startParam) return
  writeSession('startHandled', startParam)
  if (router.state.location.pathname !== '/') return
  let m = /^task_(\d+)$/.exec(startParam)
  if (m) {
    void router.navigate(`/tasks/${m[1]}`)
    return
  }
  m = /^draft_(\d+)$/.exec(startParam)
  if (m) {
    void router.navigate(`/drafts/${m[1]}`)
    return
  }
  // «Команда» в боте → задачи сотрудника
  m = /^member_(\d+)$/.exec(startParam)
  if (m) {
    void router.navigate(`/team/${m[1]}`)
    return
  }
  if (startParam === 'new') {
    void router.navigate('/tasks/new')
    return
  }
  if (startParam === 'review') {
    void router.navigate('/outbox?f=review', { replace: true })
    return
  }
  // Ссылки из бота: ящик идей, заявки, новая заявка
  const routes: Record<string, string> = {
    home: '/home',
    ideas: '/ideas',
    ideas_all: '/ideas?tab=all',
    approvals: '/approvals',
    approvals_to_me: '/approvals?tab=to_me',
    approval_new: '/approvals/new',
    shifts: '/shifts',
    team: '/team',
    outbox: '/outbox',
    report: '/reports',
    recurring: '/recurring',
    recurring_new: '/recurring/new',
  }
  if (routes[startParam]) void router.navigate(routes[startParam])
}
